import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import { HELP_AREA } from '@/helpers/constants';
import InfoMsg from '@/components/UI/InfoMsg/InfoMsg';
import infoMsgStyles from '@/components/UI/InfoMsg/InfoMsg.module.scss';
import { limitOfVolumeTabsMapping } from '@/helpers/opensearch-utils';
import { setCampaignInfo, setAffectedVehiclesValidations, setHighlightedTab } from '@/redux/Campaign/campaign.action';
import {
  getCampaignHelp, getVolumeHeaderPlants, getSelectedPlants, getCampaignVehicles, getCampaignInfo,
  getOriginalValues, getCampaignValidations, getLimitationPlants, getCampaignSearch,
  getHighlightTab,
} from '@/redux/Campaign/campaign.reducer';
import { getHeaderLanguage } from '@/redux/Header/header.reducer';
import { setAreaHelp } from '@/redux/Help/help.action';
import { AffectedVehicles, CampaignInfo, CampaignSearch, ObjLimitationPlant, OriginalValues, Validations, WKZNamePlant } from '@/types';
import VolumeActions from './VolumeActions/VolumeActions';
import VolumeCopy from './VolumeCopy/VolumeCopy';
import { validateAddedVolumes } from './VolumeDetails/VehicleCleanDirt/VehicleCleanDirt-utils';
import VolumeDetails from './VolumeDetails/VolumeDetails';
import { updateCampaignActiveLanguages } from './VolumeDetails/volumeDetails-utils';
import VolumeHeader from './VolumeHeader/VolumeHeader';
import styles from '../AffectedVehicles.module.scss';
import { isViaPlants, validateSeries } from '../SeriesPanel/SeriesRow/series-row-utils';

type VolumeProps = {
  isReadOnly: boolean;
  setHelpArea: (area: string) => void;
  headerPlants: WKZNamePlant[];
  plants: ObjLimitationPlant[];
  affectedVehicles: AffectedVehicles;
  campaignInfo: CampaignInfo;
  setInfo: (info: CampaignInfo) => void;
  originalValues: OriginalValues;
  invalid: boolean;
  setValidations: (validations: Validations) => void;
  validations: Validations;
  headerLanguage: string;
  limitationPlants: { [wkzNumber: string]: ObjLimitationPlant };
  campaignSearch: CampaignSearch;
  highlightTab: { plant: string; tab: string };
  setHighlightedTab: (tab: string | { plant: string; tab: string }) => void;
};

const Volume: React.FC<VolumeProps> = (props) => {
  const { isReadOnly, setHelpArea, headerPlants, plants, affectedVehicles, campaignInfo, setInfo, originalValues, invalid,
    setValidations, validations, headerLanguage, limitationPlants, campaignSearch, highlightTab, setHighlightedTab } = props;
  const { t } = useTranslation();
  const [tabSelected, setTabSelected] = useState(limitOfVolumeTabsMapping.vehicleGroups);
  const [plantSelected, setPlantSelected] = useState<WKZNamePlant>();
  const [showCopyData, setShowCopyData] = useState(false);
  const [manualEntry, setManualEntry] = useState(true);
  const plantsToUse = isViaPlants(affectedVehicles) ? plants.filter(({ plantName }) => plantName) : headerPlants;
  const { series, affectedPlants, volumes } = affectedVehicles;

  useEffect(() => {
    setTabSelected((highlightTab && limitOfVolumeTabsMapping[highlightTab.tab]) || limitOfVolumeTabsMapping.vehicleGroups);
    setPlantSelected((headerPlants.find(({ wkzNumber }) => wkzNumber === highlightTab?.plant)) || headerPlants[0]);
  }, [campaignSearch?.fieldSelected, highlightTab]);

  useEffect(() => {
    if (!campaignSearch?.active) {
      setHighlightedTab('');
    }
  }, [campaignSearch]);

  useEffect(() => {
    updateCampaignActiveLanguages(originalValues, plantsToUse, campaignInfo, setInfo);
    validateAddedVolumes(volumes, headerLanguage, validations, setValidations, plantsToUse, limitationPlants);
  }, [plantsToUse?.length, limitationPlants]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    validateSeries(validations, affectedVehicles, setValidations, headerLanguage);
  }, [series?.length, affectedPlants?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AccordionPanel
      title={t('affectedVehicles.volume')}
      handleClick={() => setHelpArea(HELP_AREA.VOLUME)}
      isMandatory
      invalid={invalid}
      fieldPath={['affectedVehicles', 'volumes']}
    >
      {plantsToUse?.length > 0
        ? (
          <div className={styles.VolumeContainer}>
            <VolumeHeader
              setTabSelected={setTabSelected}
              setPlantSelected={setPlantSelected}
              setShowCopyData={setShowCopyData}
              plantSelected={plantSelected}
              setManualEntry={setManualEntry}
            />
            <div className={styles.Separator} />
            <div className={styles.Flex}>
              {!showCopyData && (
                <VolumeActions

                  plantSelected={plantSelected}
                  setShowCopyData={setShowCopyData}
                  manualEntry={manualEntry}
                  setManualEntry={setManualEntry}
                />
              )}
              {showCopyData && (
                <VolumeCopy
                  plantSelected={plantSelected}
                  setShowCopyData={setShowCopyData}
                />
              )}
            </div>
            <VolumeDetails
              tabSelected={tabSelected}
              setTabSelected={setTabSelected}
              plantSelected={plantSelected}
              manualEntry={manualEntry}
              isReadOnly={isReadOnly}
            />
          </div>
        )
        : (<InfoMsg customClass={infoMsgStyles.LimitVolumeNoDataInfoMsg} msg={t('affectedVehicles.limitVolumeNoData')} />)}

    </AccordionPanel>
  );
};

const mapStateToProps = (state) => ({
  headerPlants: getVolumeHeaderPlants(state) as unknown as WKZNamePlant[],
  campaignHelp: getCampaignHelp(state),
  plants: getSelectedPlants(state) || [],
  affectedVehicles: getCampaignVehicles(state),
  limitationPlants: getLimitationPlants(state),
  campaignInfo: getCampaignInfo(state),
  originalValues: getOriginalValues(state),
  validations: getCampaignValidations(state),
  headerLanguage: getHeaderLanguage(state) || getCampaignInfo(state)?.defaultLanguage,
  campaignSearch: getCampaignSearch(state),
  highlightTab: getHighlightTab(state),
});

const actionCreators = {
  setHelpArea: setAreaHelp,
  setInfo: setCampaignInfo,
  setValidations: setAffectedVehiclesValidations,
  setHighlightedTab: setHighlightedTab,
};

export default connect(mapStateToProps, actionCreators)(Volume);
